import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const MISSION_LABELS = { pc: 'Limitée PC', elargie: 'Élargie', complete: 'Complète' };

export default function ProvisionsAssurance() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pForm, setPForm] = useState({ annee: new Date().getFullYear(), montant: 0, libelle: '' });
  const [openPay, setOpenPay] = useState(false);

  const load = async () => {
    const { data } = await api.get('/dashboard/provisions-assurance');
    setData(data);
  };

  useEffect(() => { load(); }, []);

  const addPaiement = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projets/${selected.id}/assurance-paiement`, {
        annee: Number(pForm.annee), montant: Number(pForm.montant), libelle: pForm.libelle,
      });
      toast.success('Paiement enregistré');
      setOpenPay(false);
      setPForm({ annee: new Date().getFullYear(), montant: 0, libelle: '' });
      load();
    } catch (err) { toast.error('Erreur'); }
  };

  const removePaiement = async (projet, paiementId) => {
    await api.delete(`/projets/${projet.id}/assurance-paiement/${paiementId}`);
    toast.success('Supprimé');
    load();
  };

  if (!data) return <Layout title="Provisions Assurance"><div>Chargement...</div></Layout>;

  return (
    <Layout title="Assurance — Suivi" subtitle="Par projet">
      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="provisions-table">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-900">
            <tr className="text-left">
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Code</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Projet</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Travaux</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Mission</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Estimé</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Payé</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Reste</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          {/* Compact totals row */}
          <thead className="border-b border-zinc-200 bg-zinc-50">
            <tr>
              <td className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium" colSpan={4}>TOTAUX</td>
              <td className="px-3 py-1.5 font-mono text-right text-amber-700 font-semibold">{fmtEUR(data.total_assurance)}</td>
              <td className="px-3 py-1.5 font-mono text-right text-emerald-700 font-semibold">{fmtEUR(data.total_paye)}</td>
              <td className={`px-3 py-1.5 font-mono text-right font-semibold ${data.total_reste > 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{fmtEUR(data.total_reste)}</td>
              <td></td>
            </tr>
          </thead>
          <tbody>
            {data.par_projet.filter(p => p.assurance_estime > 0).map((p) => (
              <React.Fragment key={p.id}>
                <tr className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-3 py-1.5 font-mono text-xs font-medium">{p.code}</td>
                  <td className="px-3 py-1.5 text-sm">{p.nom}</td>
                  <td className="px-3 py-1.5 font-mono text-right text-xs">{fmtEUR(p.montant_travaux)}</td>
                  <td className="px-3 py-1.5 text-xs">
                    <span className="inline-block px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 text-[10px] uppercase tracking-wider">
                      {MISSION_LABELS[p.type_mission]} {p.taux_implication}%
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-right text-xs text-amber-700">{fmtEUR(p.assurance_estime)}</td>
                  <td className="px-3 py-1.5 font-mono text-right text-xs text-emerald-700">{fmtEUR(p.assurance_payee)}</td>
                  <td className={`px-3 py-1.5 font-mono text-right text-xs font-medium ${p.assurance_reste > 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{fmtEUR(p.assurance_reste)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <Button size="sm" variant="outline" className="rounded-none border-zinc-900 h-7 text-[11px]"
                      onClick={() => { setSelected(p); setOpenPay(true); }} data-testid={`add-paiement-${p.code}`}>
                      <Plus size={10} className="mr-1" /> Paiement
                    </Button>
                  </td>
                </tr>
                {p.paiements && p.paiements.length > 0 && (
                  <tr className="bg-zinc-50">
                    <td></td>
                    <td colSpan={6} className="px-3 py-1 text-xs">
                      <div className="flex flex-wrap gap-1.5">
                        {p.paiements.map((pay) => (
                          <div key={pay.id} className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-white border border-zinc-200 text-[11px]">
                            <span className="font-mono font-medium">{pay.annee}</span>
                            <span className="font-mono text-emerald-700">{fmtEUR(pay.montant)}</span>
                            {pay.libelle && <span className="text-zinc-500">· {pay.libelle}</span>}
                            <button onClick={() => removePaiement(p, pay.id)} className="text-rose-500 hover:text-rose-700"><Trash2 size={9} /></button>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td></td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {data.par_projet.filter(p => p.assurance_estime > 0).length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-500">Aucun projet avec montant travaux. Renseignez le montant dans la fiche projet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal paiement */}
      <Dialog open={openPay} onOpenChange={setOpenPay}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-xl">Paiement assurance — {selected?.code}</DialogTitle>
          </DialogHeader>
          <form onSubmit={addPaiement} className="space-y-4">
            <div><Label>Année *</Label><Input required type="number" className="rounded-none font-mono" value={pForm.annee} onChange={(e) => setPForm({ ...pForm, annee: e.target.value })} /></div>
            <div><Label>Montant payé (€) *</Label><Input required type="number" step="0.01" className="rounded-none font-mono" value={pForm.montant} onChange={(e) => setPForm({ ...pForm, montant: e.target.value })} /></div>
            <div><Label>Libellé</Label><Input className="rounded-none" value={pForm.libelle} onChange={(e) => setPForm({ ...pForm, libelle: e.target.value })} placeholder="Prime 2025, acompte..." /></div>
            <Button type="submit" className="w-full rounded-none bg-[#002FA7] hover:bg-[#001B63]">Enregistrer</Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
